import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Home, Plus, Loader2, Search, ArrowLeft, Calendar, Users, TrendingUp,
  CheckCircle, Clock, FileText, UserPlus, Building2, Phone, Mail, Trash2
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { API } from "@/App";
import { AddInstructionModal } from "@/components/instructions/AddInstructionModal";
import { AddApplicantForm } from "@/components/applicants/AddApplicantForm";

export const SalesLandingPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Modals
  // SI-P0-04: Removed showNewListing - now redirects to /sales/instructions/list
  const [showAddApplicant, setShowAddApplicant] = useState(false);
  
  // Data
  const [salesProperties, setSalesProperties] = useState([]);
  const [recentSalesAgreed, setRecentSalesAgreed] = useState([]);
  const [newInstructions, setNewInstructions] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [diaryAppointments, setDiaryAppointments] = useState([]);
  const [staffList, setStaffList] = useState([]);
  
  // Search
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const [salesRes, instructionsRes, applicantsRes, diaryRes, staffRes] = await Promise.all([
        axios.get(`${API}/sales/cases`, { headers }).catch(() => ({ data: { cases: [] } })),
        axios.get(`${API}/instructions/all`, { headers }).catch(() => ({ data: { instructions: [] } })),
        axios.get(`${API}/applicants/all`, { headers }).catch(() => ({ data: { applicants: [] } })),
        axios.get(`${API}/diary/appointments?week_offset=0`, { headers }).catch(() => ({ data: { appointments: [] } })),
        axios.get(`${API}/diary/staff`, { headers }).catch(() => ({ data: { staff: [] } }))
      ]);
      
      const salesCases = salesRes.data.cases || [];
      const instructions = instructionsRes.data.instructions || [];
      const salesInstructions = instructions.filter(i => i.type === "sales");
      
      // Combine sales data
      const allSalesProps = [...salesInstructions];
      salesCases.forEach(c => {
        if (!salesInstructions.find(i => i.property_ref === c.case_id)) {
          allSalesProps.push({
            instruction_id: c.case_id,
            property_ref: c.case_id,
            full_address: c.address_line1 || c.property_address || c.address || 'Unknown',
            asking_price: c.offer_price || c.asking_price,
            status: c.status,
            type: "sales",
            created_at: c.created_at
          });
        }
      });
      
      setSalesProperties(allSalesProps);
      
      // Recent Sales Agreed (SSTC, Under Offer)
      const agreed = salesCases.filter(c => ['sstc', 'under_offer', 'exchanged'].includes(c.status))
        .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
        .slice(0, 10);
      setRecentSalesAgreed(agreed);
      
      // New Instructions (recent listings)
      const recent = [...allSalesProps]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 10);
      setNewInstructions(recent);
      
      // Applicants
      setApplicants(applicantsRes.data?.applicants || []);
      
      // Today's diary
      setDiaryAppointments(diaryRes.data?.appointments || []);
      
      // Staff
      setStaffList(staffRes.data?.staff || []);
      
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { label: "Active", className: "bg-green-100 text-green-700" },
      under_offer: { label: "Under Offer", className: "bg-amber-100 text-amber-700" },
      sstc: { label: "SSTC", className: "bg-blue-100 text-blue-700" },
      exchanged: { label: "Exchanged", className: "bg-purple-100 text-purple-700" },
      completed: { label: "Completed", className: "bg-emerald-100 text-emerald-700" },
      withdrawn: { label: "Withdrawn", className: "bg-slate-100 text-slate-600" },
      draft: { label: "Draft", className: "bg-slate-100 text-slate-600" },
    };
    const c = config[status] || { label: status || "Unknown", className: "bg-slate-100 text-slate-600" };
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const formatPrice = (price) => {
    if (!price) return "POA";
    return `£${Number(price).toLocaleString()}`;
  };

  const handleDeleteApplicant = async (applicant) => {
    if (!confirm(`Are you sure you want to delete "${applicant.display_name}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/applicants/sales/${applicant.applicant_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Applicant "${applicant.display_name}" deleted successfully`);
      // Refresh the applicants list
      setApplicants(prev => prev.filter(a => a.applicant_id !== applicant.applicant_id));
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to delete applicant";
      toast.error(message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/home")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold">Sales</h1>
              <p className="text-sm text-muted-foreground">Welcome, {user.name || "Team"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowAddApplicant(true)}>
              <UserPlus className="w-4 h-4 mr-2" /> Add Applicant
            </Button>
            {/* SI-P0-04: Redirect to Sales Instructions module */}
            <Button onClick={() => navigate('/sales/instructions/list')}>
              <Plus className="w-4 h-4 mr-2" /> New Instruction
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border p-1">
            <TabsTrigger value="dashboard" className="gap-2">
              <Home className="w-4 h-4" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="applicants" className="gap-2">
              <Users className="w-4 h-4" /> Applicants
            </TabsTrigger>
            <TabsTrigger value="agreed" className="gap-2">
              <CheckCircle className="w-4 h-4" /> Sales Agreed
            </TabsTrigger>
            <TabsTrigger value="instructions" className="gap-2">
              <FileText className="w-4 h-4" /> New Instructions
            </TabsTrigger>
            <TabsTrigger value="progression" className="gap-2">
              <TrendingUp className="w-4 h-4" /> Sales Progression
            </TabsTrigger>
            <TabsTrigger value="diary" className="gap-2">
              <Calendar className="w-4 h-4" /> Office Diary
            </TabsTrigger>
          </TabsList>

          {/* DASHBOARD TAB */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats Row */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("applicants")}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold">{applicants.length}</p>
                      <p className="text-sm text-muted-foreground">Applicants</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("agreed")}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold">{recentSalesAgreed.length}</p>
                      <p className="text-sm text-muted-foreground">Sales Agreed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("instructions")}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold">{salesProperties.length}</p>
                      <p className="text-sm text-muted-foreground">Properties</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("diary")}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold">{diaryAppointments.length}</p>
                      <p className="text-sm text-muted-foreground">Today's Appointments</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick View Panels */}
            <div className="grid grid-cols-2 gap-6">
              {/* Recent Sales Agreed */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-amber-500" />
                    Recent Sales Agreed
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {recentSalesAgreed.slice(0, 5).map((sale, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 rounded hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/sales/case/${sale.case_id}`)}>
                      <div>
                        <p className="font-medium text-sm">{sale.address_line1 || sale.property_address}</p>
                        <p className="text-xs text-muted-foreground">{formatPrice(sale.offer_price || sale.asking_price)}</p>
                      </div>
                      {getStatusBadge(sale.status)}
                    </div>
                  ))}
                  {recentSalesAgreed.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">No sales agreed recently</p>
                  )}
                </CardContent>
              </Card>

              {/* New Instructions */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-green-500" />
                    New Instructions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {newInstructions.slice(0, 5).map((inst, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 rounded hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/sales/case/${inst.instruction_id}`)}>
                      <div>
                        <p className="font-medium text-sm">{inst.full_address}</p>
                        <p className="text-xs text-muted-foreground">{formatPrice(inst.asking_price)}</p>
                      </div>
                      {getStatusBadge(inst.status)}
                    </div>
                  ))}
                  {newInstructions.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">No new instructions</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* APPLICANTS TAB */}
          <TabsContent value="applicants" className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search applicants..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
                <p className="text-sm text-muted-foreground">{applicants.length} applicants</p>
              </div>
              <Button onClick={() => setShowAddApplicant(true)}>
                <UserPlus className="w-4 h-4 mr-2" /> Add Applicant
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left py-3 px-4 font-medium text-sm">Name</th>
                        <th className="text-left py-3 px-4 font-medium text-sm">Contact</th>
                        <th className="text-left py-3 px-4 font-medium text-sm">Budget</th>
                        <th className="text-left py-3 px-4 font-medium text-sm">Property</th>
                        <th className="text-center py-3 px-4 font-medium text-sm">Status</th>
                        <th className="text-center py-3 px-4 font-medium text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applicants
                        .filter(a => !searchTerm || 
                          a.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          a.primary_email?.toLowerCase().includes(searchTerm.toLowerCase()))
                        .slice(0, 50)
                        .map((applicant) => (
                          <tr key={applicant.applicant_id} className="border-b hover:bg-slate-50">
                            <td className="py-3 px-4 cursor-pointer" onClick={() => navigate(`/sales/applicant/${applicant.applicant_id}`)}>
                              <p className="font-medium">{applicant.display_name}</p>
                            </td>
                            <td className="py-3 px-4 text-sm text-muted-foreground cursor-pointer" onClick={() => navigate(`/sales/applicant/${applicant.applicant_id}`)}>
                              <div className="flex items-center gap-2">
                                {applicant.primary_email && <Mail className="w-3 h-3" />}
                                {applicant.primary_phone && <Phone className="w-3 h-3" />}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm cursor-pointer" onClick={() => navigate(`/sales/applicant/${applicant.applicant_id}`)}>
                              {applicant.budget_max ? `Up to ${formatPrice(applicant.budget_max)}` : '-'}
                            </td>
                            <td className="py-3 px-4 text-sm cursor-pointer" onClick={() => navigate(`/sales/applicant/${applicant.applicant_id}`)}>
                              {applicant.case_address || '-'}
                            </td>
                            <td className="py-3 px-4 text-center cursor-pointer" onClick={() => navigate(`/sales/applicant/${applicant.applicant_id}`)}>
                              <Badge variant="outline">{applicant.buyer_status || applicant.role || 'Active'}</Badge>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteApplicant(applicant);
                                }}
                                data-testid={`delete-applicant-${applicant.applicant_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SALES AGREED TAB */}
          <TabsContent value="agreed" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-amber-500" />
                  Recent Sales Agreed
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left py-3 px-4 font-medium text-sm">Property</th>
                        <th className="text-left py-3 px-4 font-medium text-sm">Price</th>
                        <th className="text-left py-3 px-4 font-medium text-sm">Buyer</th>
                        <th className="text-center py-3 px-4 font-medium text-sm">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentSalesAgreed.map((sale, idx) => (
                        <tr key={idx} className="border-b hover:bg-slate-50 cursor-pointer"
                          onClick={() => navigate(`/sales/case/${sale.case_id}`)}>
                          <td className="py-3 px-4">
                            <p className="font-medium">{sale.address_line1 || sale.property_address}</p>
                            <p className="text-xs text-muted-foreground">{sale.postcode}</p>
                          </td>
                          <td className="py-3 px-4">
                            <p className="font-semibold text-blue-600">{formatPrice(sale.offer_price || sale.asking_price)}</p>
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {sale.buyer_name || '-'}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {getStatusBadge(sale.status)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* NEW INSTRUCTIONS TAB */}
          <TabsContent value="instructions" className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">Recent instructions and new listings</p>
              {/* SI-P0-04: Redirect to Sales Instructions module */}
              <Button onClick={() => navigate('/sales/instructions/list')}>
                <Plus className="w-4 h-4 mr-2" /> New Instruction
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left py-3 px-4 font-medium text-sm">Property</th>
                        <th className="text-left py-3 px-4 font-medium text-sm">Asking Price</th>
                        <th className="text-left py-3 px-4 font-medium text-sm">Date Added</th>
                        <th className="text-center py-3 px-4 font-medium text-sm">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newInstructions.map((inst, idx) => (
                        <tr key={idx} className="border-b hover:bg-slate-50 cursor-pointer"
                          onClick={() => navigate(`/sales/case/${inst.instruction_id}`)}>
                          <td className="py-3 px-4">
                            <p className="font-medium">{inst.full_address}</p>
                          </td>
                          <td className="py-3 px-4">
                            <p className="font-semibold text-blue-600">{formatPrice(inst.asking_price)}</p>
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {inst.created_at ? format(parseISO(inst.created_at), "dd MMM yyyy") : '-'}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {getStatusBadge(inst.status)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SALES PROGRESSION TAB */}
          <TabsContent value="progression" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-500" />
                  Sales Progression
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">Track and manage your sales through to completion</p>
                <Button onClick={() => navigate("/sales/pipeline")}>
                  Open Full Sales Progression
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* OFFICE DIARY TAB */}
          <TabsContent value="diary" className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Today's Appointments</h2>
              <Button onClick={() => navigate("/diary")}>
                Open Full Diary
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                {diaryAppointments.length === 0 ? (
                  <div className="py-12 text-center">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p className="text-muted-foreground">No appointments today</p>
                    <Button variant="outline" className="mt-4" onClick={() => navigate("/diary")}>
                      View Full Diary
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y">
                    {diaryAppointments.slice(0, 10).map((apt, idx) => (
                      <div key={idx} className="p-4 hover:bg-slate-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{apt.title}</p>
                            <p className="text-sm text-muted-foreground">{apt.address || apt.location}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium">{apt.start_time ? format(parseISO(apt.start_time), "HH:mm") : ''}</p>
                            <p className="text-xs text-muted-foreground">{apt.user_name}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* SI-P0-04: Removed New Listing Modal - now using /sales/instructions/list */}

      {/* Add Applicant Modal - Comprehensive Form */}
      <AddApplicantForm
        open={showAddApplicant}
        onClose={() => setShowAddApplicant(false)}
        onSuccess={fetchData}
        staffList={staffList}
      />
    </div>
  );
};

export default SalesLandingPage;
